import { useEffect, useRef, useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import type { Measurement } from '../../../domain/Measurement';
import { componentDisplayName } from '../../../domain/Component';
import type { IrrigationEvent } from '../../../domain/Irrigation';
import type { IrrigationDailySummary } from '../../ports/IrrigationRepository';

export interface LinkedProbe {
  device: Device;
  channel: number;
  name: string;
}

// Historial de una válvula de la estación. valve es null cuando todavía no
// hay ninguna elegida — en ese caso no se pide nada y se limpia el estado.
export function useIrrigationHistory(valve: { deviceId: string; channel: number } | null) {
  const { irrigation, devices: deviceRepo, measurements: measurementRepo } = useRepositories();
  const deviceId = valve?.deviceId ?? null;
  const channel = valve?.channel ?? null;

  const [device, setDevice] = useState<Device | null>(null);
  const [events, setEvents] = useState<IrrigationEvent[]>([]);
  const [dailyLiters, setDailyLiters] = useState<IrrigationDailySummary[]>([]);
  // Si la válvula tiene una sonda de suelo vinculada, se superponen los
  // riegos sobre el gráfico de humedad de ESA sonda — así se ve el efecto
  // real (sube la humedad después de regar), no solo cuánto se regó.
  const [probe, setProbe] = useState<LinkedProbe | null>(null);
  const [humidityMeasurements, setHumidityMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (id: string, ch: number) => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    try {
      const [{ device: found }, history, { schedule }] = await Promise.all([
        deviceRepo.get(id),
        irrigation.getHistory(id, ch),
        irrigation.getSchedule(id, ch),
      ]);
      setDevice(found);
      setEvents(history.events);
      setDailyLiters(history.dailyLiters);

      if (schedule?.humidityDeviceId) {
        const probeChannel = schedule.humidityChannel ?? 1;
        const [{ device: hDevice }, { measurements }] = await Promise.all([
          schedule.humidityDeviceId === id
            ? Promise.resolve({ device: found })
            : deviceRepo.get(schedule.humidityDeviceId),
          measurementRepo.listForDevice(schedule.humidityDeviceId, 300, {
            metric: 'soil_moisture',
            channel: probeChannel,
          }),
        ]);
        const component = hDevice.components.find((c) => c.kind === 'soil' && c.channel === probeChannel);
        setProbe({
          device: hDevice,
          channel: probeChannel,
          name: component ? componentDisplayName(component) : `Sonda de suelo ${probeChannel}`,
        });
        setHumidityMeasurements(measurements);
      } else {
        setProbe(null);
        setHumidityMeasurements([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el historial de riego.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  };

  // useEffect cubre el montaje inicial (y hace este hook testeable con
  // renderHook, que no dispara ciclo de vida de Ionic). useIonViewWillEnter
  // cubre lo que useEffect solo no puede: Ionic mantiene esta página viva en
  // el DOM al navegar, así que sin esto volver a ver el historial mostraba
  // lo que había la primera vez que se entró, no los riegos nuevos.
  useEffect(() => {
    if (!deviceId || !channel) {
      setDevice(null);
      setEvents([]);
      setDailyLiters([]);
      setProbe(null);
      setHumidityMeasurements([]);
      loadedRef.current = true;
      setLoading(false);
      return;
    }
    load(deviceId, channel);
  }, [deviceId, channel, deviceRepo, irrigation, measurementRepo]);

  useIonViewWillEnter(() => {
    if (deviceId && channel) load(deviceId, channel);
  });

  const valveComponent = device?.components.find((c) => c.kind === 'valve' && c.channel === channel);
  const valveName = valveComponent ? componentDisplayName(valveComponent) : channel ? `Sector ${channel}` : '';

  return {
    device,
    valveName,
    events,
    dailyLiters,
    probe,
    humidityMeasurements,
    loading,
    error,
  };
}
